import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { observable, toJS } from "mobx";
import { Icon } from "@blueprintjs/core";
import { ItemRenderer, MultiSelect } from "@blueprintjs/select";
import Toast from 'helpers/toaster';

const initials = (name = '') =>
  String(name)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

const looksLikeEmail = (text) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(text).trim());

@inject("contactsStore")
@observer
class MultipleEmailSelector extends Component {

  @observable query = "";

  componentDidMount() {
    const {
      onContactSelect,
      queryContacts,
      props: {
        contacts,
        contactsStore
      }
    } = this;
    contactsStore.selectedContacts = [];
    queryContacts("", null);
    if(contacts && contacts.length > 0 && Array.isArray(contacts)){
      contacts.map((contact) => {
        onContactSelect(contact);
      })
    }
  }

  onTagKeyDown = (event, index) => {
    // 9 = TAB
    if (event.keyCode === 9) {
      if(event.target.value){
        this.addNewEmail({
          name: event.target.value,
          email: event.target.value
        });
      }
      this.query = "";
    } else if(event.keyCode === 188){ // 188 = , (comma)
      if(event.target.value){
        event.preventDefault();
        event.target.value.split(",").map((email) => {
          this.addNewEmail({
            name: email,
            email: email
          });
        }) 
      }
      this.query = "";
    }
  }

  onTagChange = (value) => {
    this.contact = value;
  }
  
  onContactSelect = (contact) => {
    this.addNewEmail(contact);
    this.query = "";
  }

  queryContacts = (query, event) => {
    let {
      props: {
        contactsStore,
        contactsStore: {
          contacts
        }
      }
    } = this;
    this.query = query;
    contacts.isLoading = true;
    contactsStore.getContacts(query || "*")
      .then(response => {
        contacts.objects = response.data.data;
      })
      .catch(error => {
        
      })
      .finally(() => {
        contacts.isLoading = false;
      })
  }

  escapeRegExpChars = (text) => {
    return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
  }

  highlightText = (text, query) => {
    let lastIndex = 0;
    const words = query
        .split(/\s+/)
        .filter(word => word.length > 0)
        .map(this.escapeRegExpChars);
    if (words.length === 0) {
        return [text];
    }
    const regexp = new RegExp(words.join("|"), "gi");
    const tokens: React.ReactNode[] = [];
    while (true) {
        const match = regexp.exec(text);
        if (!match) {
            break;
        }
        const length = match[0].length;
        const before = text.slice(lastIndex, regexp.lastIndex - length);
        if (before.length > 0) {
            tokens.push(before);
        }
        lastIndex = regexp.lastIndex;
        tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
    }
    const rest = text.slice(lastIndex);
    if (rest.length > 0) {
        tokens.push(rest);
    }
    return tokens;
  }

  handleTagRemove = (_tag, index) => {
    this.deselectContact(index);
  }

  deselectContact = (index) => {
    let {
      props: {
        contactsStore,
        contactsStore: {
          selectedContacts
        },
        onEmailChange
      }
    } = this;
    contactsStore.selectedContacts = contactsStore.selectedContacts.filter((_contact, i) => i !== index);
    if(onEmailChange){
      onEmailChange(contactsStore.selectedContacts);
    }
  }

  addNewEmail = (contact) => {
    let {
      props: {
        contactsStore: {
          selectedContacts
        },
        onEmailChange
      }
    } = this;
    selectedContacts.push(contact);
    if(onEmailChange){
      onEmailChange(selectedContacts);
    }
  }

  render() {
    const {
      props: {
        contactsStore: {
          contacts,
          selectedContacts
        }
      },
      onContactSelect,
      handleTagRemove
    } = this;
    return(
      <React.Fragment>

        <MultiSelect
          onQueryChange={this.queryContacts}
          itemRenderer={(contact, { handleClick, modifiers, query }) => {
            if (selectedContacts.filter((_selectedContact) => _selectedContact.email === contact.email ).length > 0) {
              return null;
            }
            const name = contact.name && contact.name !== contact.email ? contact.name : null;
            return (
              <li key={contact.email + contact.name}>
                <div
                  role="option"
                  aria-selected={modifiers.active}
                  className={`contact-suggest-option${modifiers.active ? ' is-active' : ''}`}
                  onClick={handleClick}
                >
                  <span className="contact-suggest-avatar" aria-hidden="true">{initials(name || contact.email)}</span>
                  <span className="contact-suggest-text">
                    <span className="contact-suggest-name">{this.highlightText(name || contact.email, query)}</span>
                    {name && <span className="contact-suggest-email">{this.highlightText(contact.email, query)}</span>}
                  </span>
                </div>
              </li>
            );
          }}
          createNewItemFromQuery={(query) => { 
            return { email: query, name: query } 
          } }
          createNewItemRenderer={(query, active, handleClick) => {
            if (!query) return null;
            // Only a whole address can be added; until then, say so.
            if (!looksLikeEmail(query)) {
              return (
                <li key="create-hint" className="contact-suggest-hint">
                  Type a full email address to send to someone new.
                </li>
              );
            }
            return (
              <li key={query}>
                <div
                  role="option"
                  aria-selected={active}
                  className={`contact-suggest-option is-new${active ? ' is-active' : ''}`}
                  onClick={handleClick}
                >
                  <span className="contact-suggest-avatar" aria-hidden="true"><Icon icon="plus" iconSize={12} /></span>
                  <span className="contact-suggest-text">
                    <span className="contact-suggest-name">Send to {query.trim()}</span>
                    <span className="contact-suggest-email">Not in your contacts</span>
                  </span>
                </div>
              </li>
            );
          }}
          onItemsPaste={(contacts) => {
            contacts.map((contact) => {
              if(!selectedContacts.filter((_selectedContact) => _selectedContact.email === contact.email ).length > 0) {
                onContactSelect(contact);
              }
            });
          }}
          query={this.query}
          tagRenderer={(contact) => (
            contact.name && contact.name !== contact.email
              ? <span title={contact.email}>{contact.name} <span className="contact-tag-email">{contact.email}</span></span>
              : contact.email
          )}
          popoverProps={{ minimal: true, popoverClassName: 'contact-suggest-popover' }}
          items={toJS(contacts.objects)}
          selectedItems={toJS(selectedContacts)}
          onItemSelect={onContactSelect}
          fill={true}
          openOnKeyDown={true}
          tagInputProps={{ 
            onRemove: handleTagRemove.bind(this), placeholder: selectedContacts.length ? '' : 'Add a name or email',
            addOnBlur: true,
            onKeyDown: this.onTagKeyDown
          }}
        />
      </React.Fragment>
    )
  }

}

export default MultipleEmailSelector;
